import { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PdfViewerProps {
  fileUrl: string;
  mimeType: string;
}

export function PdfViewer({ fileUrl, mimeType }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [renderScale, setRenderScale] = useState(1.0);
  const [cssZoom, setCssZoom] = useState(1.0);
  const [error, setError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const targetScaleRef = useRef(1.0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const scale = renderScale * cssZoom;

  const commitScale = (newScale: number) => {
    setRenderScale(newScale);
    setCssZoom(1.0);
    targetScaleRef.current = newScale;
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const clamped = Math.min(3.0, Math.max(0.25, targetScaleRef.current - e.deltaY * 0.01));
      targetScaleRef.current = clamped;
      // Instant visual feedback via CSS transform
      setCssZoom(clamped / renderScale);
      // Debounce the expensive re-render
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => commitScale(clamped), 200);
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      clearTimeout(debounceRef.current);
    };
  }, [renderScale]);

  // For images, just render an img tag
  if (mimeType.startsWith('image/')) {
    return (
      <div className="h-full overflow-auto bg-muted p-4">
        <img src={fileUrl} alt="Uploaded document" className="w-full h-auto" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Controls */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-sm text-muted-foreground">
          {numPages} {numPages === 1 ? 'page' : 'pages'}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => commitScale(Math.max(0.25, targetScaleRef.current - 0.1))}
            className="rounded px-2 py-1 text-sm hover:bg-muted"
          >
            -
          </button>
          <span className="text-sm text-muted-foreground">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => commitScale(Math.min(3.0, targetScaleRef.current + 0.1))}
            className="rounded px-2 py-1 text-sm hover:bg-muted"
          >
            +
          </button>
        </div>
      </div>

      {/* PDF content */}
      <div
        ref={contentRef}
        className="flex-1 overflow-auto bg-muted p-4"
      >
        {error && (
          <div className="mb-4 rounded bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <div
          style={{
            transform: cssZoom !== 1 ? `scale(${cssZoom})` : undefined,
            transformOrigin: 'top left',
            transition: cssZoom === 1 ? 'transform 0.1s ease-out' : undefined,
          }}
        >
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setError(null);
            }}
            onLoadError={(err) => {
              setError(err.message);
            }}
            loading={<p className="text-sm text-muted-foreground">Loading PDF...</p>}
            error={<p className="text-sm text-destructive">Failed to load PDF. Check browser console for details.</p>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page key={i + 1} pageNumber={i + 1} scale={renderScale} className="mb-4" />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
}
